import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import StudentReportList from './StudentReportList';
import { Button, Spinner, TextInput } from 'flowbite-react';
import { TbSearch } from 'react-icons/tb';
import { debounce, get } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { Loader } from 'ui';
import { useGetAllUserByUniversity } from 'src/hooks/user.hooks';
import { GetUsersForUniversityAPIResponse } from 'types';
import UniversityFilterDrawer from './filterDrawer/UniversityFilterDrawer';
import UniversityStudentsFilters from './UniversityStudentFilters';
import { useUserContext } from 'src/context/UserProvider';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { useMutation } from 'react-query';
import { downloadUniversityStudents } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { downloadCSV } from 'src/utils/downloadCsv';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';

const StudentReport = () => {
	const toast = useToast();
	const [searchParams, setSearchParams] = useSearchParams();
	const [filterApplied, setFilterApplied] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [, setShowFilterDrawer] = useState(false);
	const search = searchParams.get('search') || '';
	const studentHandler = searchParams.get('student');
	const { universityStudentsFilters } = useUserContext();

	const searchChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLInputElement>) => {
		searchParams.set('search', event.target.value);
		setSearchParams(searchParams);
		setPage(0);
		setRowsPerPage(10);
	};

	const {
		data: studentsData,
		isLoading: isStudentsLoading,
		isFetched,
	} = useGetAllUserByUniversity({
		...universityStudentsFilters,
		search,
		page,
		filterLimit: rowsPerPage,
	});

	const downloadUniversityStudentsFn = useMutation(['downloadUniversityStudents'], downloadUniversityStudents, {
		onSuccess(data) {
			const csvData = data?.csvData;
			downloadCSV({
				data: csvData,
				columns: [
					'name',
					'email',
					'phone',
					'publicProfileLink',
					'credibility',
					'degree',
					'specialization',
					'percentage',
					'scoreType',
					'assessmentType',
					'overallScore',
					'predictedSalary',
					'technicalScore',
					'aptitudeScore',
					'softSkillScore',
					'theoreticalScore',
				],
			});
			toast({
				title: 'CSV Downloaded Successfully!',
				type: 'success',
			});
		},
		onError(error: any) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured while downloading CSV ${errorMessage}`,
				type: 'error',
			});
		},
	});

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchChangeHandler = useCallback(debounce(searchChangeHandler, 1000), [search]);

	if (isStudentsLoading && !search && !filterApplied && !isFetched) {
		return (
			<div className="flex items-center justify-center relative top-[25vh]">
				{/* <Loader /> */}
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[25%] mx-auto " />
			</div>
		);
	}
	return (
		<div className="p-4">
			{studentHandler ? null : (
				<div className="w-full md:mt-0 md:max-w-[1440px] mx-auto flex flex-wrap items-center gap-[12px] justify-stretch">
					{/* Search Input */}
					<TextInput
						icon={() => <TbSearch color="#C7C9D9" />}
						className="w-full [&_input]:bg-white max-w-[522px]"
						placeholder="Search student by name"
						name="search"
						defaultValue={search}
						rightIcon={() => (isStudentsLoading && search ? <Spinner /> : null)}
						onChange={e => debouncedSearchChangeHandler(e)}
					/>
					{/* Filters Drawer */}
					<UniversityFilterDrawer
						filterDrawerHeader={
							<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4 md:border-b md:pb-4">
								Filter by
							</h1>
						}
						filterDrawerBody={(filter: any) => {
							return (
								<div className="px-3 py-4">
									<UniversityStudentsFilters
										onApply={() => {
											setPage(0);
											filter.setFilterApplied(true);
											setFilterApplied(true);
										}}
										onClear={() => {
											setPage(0);
											filter.setFilterApplied(false);
											setFilterApplied(false);
										}}
										onClose={() => {
											filter.setShowFilterDrawer(false);
											setShowFilterDrawer(false);
										}}
									/>
								</div>
							);
						}}
					/>

					<Button
						color="light"
						disabled={downloadUniversityStudentsFn.isLoading}
						isProcessing={downloadUniversityStudentsFn.isLoading}
						onClick={() => {
							downloadUniversityStudentsFn.mutate({
								...universityStudentsFilters,
								search,
							});
						}}
					>
						Download CSV
					</Button>
				</div>
			)}
			<StudentReportList
				students={studentsData as GetUsersForUniversityAPIResponse}
				count={studentsData?.total || 0}
				page={page}
				setPage={setPage}
				rowsPerPage={rowsPerPage}
				setRowsPerPage={setRowsPerPage}
			/>
		</div>
	);
};

export default StudentReport;
